/**
 * Analytics Service
 * Frontend service for analytics and consumer insights
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface AnalyticsOverview {
  totalScans: number;
  totalConsumptions: number;
  totalProducts: number;
  engagementRate: number;
  avgRating: number;
  fraudAlerts: number;
  // Gas consumption data
  totalGasTransactions?: number;
  totalGasCostHBAR?: number;
  totalGasCostUSD?: number;
  totalGasCostINR?: number;
}

export interface GasAnalytics {
  totalGasUsed: number;
  totalCostHBAR: number;
  totalCostUSD: number;
  totalCostINR: number;
  averageCostPerOperation: {
    hbar: number;
    usd: number;
    inr: number;
  };
  costTrends: Array<{
    date: string;
    totalCostHBAR: number;
    totalCostUSD: number;
    totalCostINR: number;
    transactionCount: number;
  }>;
  operationBreakdown: Array<{
    operationType: string;
    transactionCount: number;
    totalCostHBAR: number;
    totalCostUSD: number;
    totalCostINR: number;
    averageCostHBAR: number;
    averageCostUSD: number;
    averageCostINR: number;
  }>;
  benchmarkComparison?: BenchmarkData;
}

export interface BenchmarkData {
  manufacturerAverage: {
    hbar: number;
    usd: number;
    inr: number;
  };
  platformAverage: {
    hbar: number;
    usd: number;
    inr: number;
  };
  percentileRanking: number;
  costEfficiencyRating: 'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'BELOW_AVERAGE' | 'POOR';
}

export interface EnhancedSystemStatistics {
  totalProducts: number;
  activatedProducts: number;
  consumedProducts: number;
  totalScans: number;
  totalBatches: number;
  totalDealers: number;
  totalPackages: number;
  activatedPackages: number;
  activationRate: number;
  consumptionRate: number;
  scanEngagementRate: number;
  packageActivationRate: number;
  periodComparison?: {
    previousPeriod: EnhancedSystemStatistics;
    growthMetrics: {
      productsGrowth: number;
      activationsGrowth: number;
      consumptionsGrowth: number;
      scansGrowth: number;
    };
  };
}

export interface TransactionCost {
  id: string;
  transactionHash: string;
  operationType: string;
  gasUsed: number;
  hbarCost: number;
  usdCost: number;
  inrCost: number;
  timestamp: Date;
  status: string;
  productId?: string;
  batchId?: string;
  dealerId?: string;
}

export interface ExchangeRates {
  hbarToUsd: number;
  hbarToInr: number;
  usdToInr: number;
  timestamp: string;
  source: string;
}

export interface GeographyData {
  city: string;
  state: string;
  scanCount: number;
  consumptionCount: number;
  averageRiskScore?: number;
  highRiskScans?: number;
}

export interface ConsumerInsight {
  totalConsumers: number;
  withEmail: number;
  withPhone: number;
  avgRating: number;
  feedbackCount: number;
}

export interface ProductPerformance {
  productName: string;
  batchId: string;
  totalScans: number;
  consumptions: number;
  avgRating: number;
  averageRiskScore?: number;
  anomalyFlags?: number;
}

export interface FunnelAnalytics {
  totalProducts: number;
  activatedProducts: number;
  consumedProducts: number;
  scannedProducts: number;
  scanEvents: number;
  repeatScannedProducts: number;
  feedbackScans: number;
}

export interface BatchPerformance {
  batchId: string;
  batchTitle?: string | null;
  productName: string;
  totalProducts: number;
  activatedProducts: number;
  consumedProducts: number;
  scannedProducts: number;
  scanEvents: number;
  repeatScannedProducts: number;
  avgRating: number | null;
  anomalyScans: number;
  lastScanAt?: Date | null;
}

export interface MarketGrowthRow {
  city: string;
  state: string;
  currentScans: number;
  previousScans: number;
  growthPct: number | null;
  isNewMarket: boolean;
}

export interface MarketExpansionInsights {
  topGrowthMarkets: MarketGrowthRow[];
  scanHourDistribution: Array<{ hour: number; count: number }>;
  repeatScanRate: {
    scannedProducts: number;
    repeatScannedProducts: number;
    repeatRate: number;
  };
}

export interface ConsumptionTrend {
  date: string;
  count: number;
}

export interface HeatmapPoint {
  lat: number;
  lng: number;
  count: number;
  city?: string | null;
  state?: string | null;
  firstScanAt?: string | null;
  lastScanAt?: string | null;
  avgRiskScore?: number | null;
  riskLevel?: 'low' | 'medium' | 'high' | null;
}

export interface HeatmapData {
  scanPoints: HeatmapPoint[];
  fraudPoints: HeatmapPoint[];
}

export interface FraudAlert {
  productId: string;
  batchId: string;
  serialNumber: number;
  productName: string;
  outerQrToken?: string | null;
  innerQrToken?: string | null;
  scanCount: number;
  uniqueCities: number;
  lastCity?: string | null;
  lastScan: Date;
  flagRiskLevel?: string | null;
  flagRiskScore?: number | null;
  flagReasons?: string[] | null;
  flagStatus?: string | null;
}

export interface Feedback {
  productName: string;
  rating: number;
  feedback: string;
  city: string | null;
  scannedAt: Date;
}

export interface AnalyticsQueryFilters {
  batchId?: string;
  anomalyOnly?: boolean;
}

export interface PremiumAnalyticsPricing {
  operationType: string;
  amount: number;
  currency: string;
  formatted: string;
  validityDays: number;
  consentText: string;
  baseRate?: number;
  baseCoverageMonths?: number;
  monthlySurchargePercent?: number;
}

export interface PremiumAnalyticsReportMetadata {
  days?: number;
  filters?: AnalyticsQueryFilters;
  price?: number;
  currency?: string;
  operationType?: string;
}

export interface PremiumAnalyticsReport {
  id: string;
  reportType: string;
  status: string;
  generatedAt: string;
  validUntil: string;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  payload: {
    funnel: FunnelAnalytics;
    batchPerformance: BatchPerformance[];
    marketInsights: MarketExpansionInsights;
    fraudAlerts: FraudAlert[];
    heatmapData: HeatmapData;
    geography: GeographyData[];
  };
  metadata?: PremiumAnalyticsReportMetadata;
}

class AnalyticsService {
  private getAuthHeader() {
    const token = localStorage.getItem('auth_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  private buildAnalyticsParams(filters?: AnalyticsQueryFilters) {
    const params = new URLSearchParams();
    if (filters?.batchId) params.append('batchId', filters.batchId);
    if (filters?.anomalyOnly) params.append('anomalyOnly', 'true');
    return params;
  }

  /**
   * Get analytics overview
   */
  async getOverview(filters?: AnalyticsQueryFilters): Promise<AnalyticsOverview> {
    const params = this.buildAnalyticsParams(filters);
    const response = await axios.get(
      `${API_URL}/analytics/overview${params.toString() ? `?${params.toString()}` : ''}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get geographic distribution
   */
  async getGeography(limit: number = 10, filters?: AnalyticsQueryFilters): Promise<GeographyData[]> {
    const params = this.buildAnalyticsParams(filters);
    params.set('limit', limit.toString());
    const response = await axios.get(
      `${API_URL}/analytics/geography?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get scan and fraud heatmap data
   */
  async getHeatmapData(days: number = 30, filters?: AnalyticsQueryFilters): Promise<HeatmapData> {
    const params = this.buildAnalyticsParams(filters);
    params.set('days', days.toString());
    const response = await axios.get(
      `${API_URL}/analytics/heatmap?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get consumer insights
   */
  async getConsumerInsights(filters?: AnalyticsQueryFilters): Promise<ConsumerInsight> {
    const params = this.buildAnalyticsParams(filters);
    const response = await axios.get(
      `${API_URL}/analytics/consumers${params.toString() ? `?${params.toString()}` : ''}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get product performance
   */
  async getProductPerformance(limit: number = 10, filters?: AnalyticsQueryFilters): Promise<ProductPerformance[]> {
    const params = this.buildAnalyticsParams(filters);
    params.set('limit', limit.toString());
    const response = await axios.get(
      `${API_URL}/analytics/products?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get growth funnel analytics
   */
  async getFunnelAnalytics(days: number = 30, filters?: AnalyticsQueryFilters): Promise<FunnelAnalytics> {
    const params = this.buildAnalyticsParams(filters);
    params.set('days', days.toString());
    const response = await axios.get(
      `${API_URL}/analytics/funnel?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get batch performance analytics
   */
  async getBatchPerformance(
    limit: number = 10,
    days: number = 30,
    filters?: AnalyticsQueryFilters
  ): Promise<BatchPerformance[]> {
    const params = this.buildAnalyticsParams(filters);
    params.set('limit', limit.toString());
    params.set('days', days.toString());
    const response = await axios.get(
      `${API_URL}/analytics/batch-performance?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get market expansion insights
   */
  async getMarketInsights(days: number = 30, filters?: AnalyticsQueryFilters): Promise<MarketExpansionInsights> {
    const params = this.buildAnalyticsParams(filters);
    params.set('days', days.toString());
    const response = await axios.get(
      `${API_URL}/analytics/market-insights?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get consumption trends
   */
  async getConsumptionTrends(days: number = 30, filters?: AnalyticsQueryFilters): Promise<ConsumptionTrend[]> {
    const params = this.buildAnalyticsParams(filters);
    params.set('days', days.toString());
    const response = await axios.get(
      `${API_URL}/analytics/trends?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get fraud alerts
   */
  async getFraudAlerts(filters?: AnalyticsQueryFilters): Promise<FraudAlert[]> {
    const params = this.buildAnalyticsParams(filters);
    const response = await axios.get(
      `${API_URL}/analytics/fraud-alerts${params.toString() ? `?${params.toString()}` : ''}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get recent feedback
   */
  async getRecentFeedback(limit: number = 10, filters?: AnalyticsQueryFilters): Promise<Feedback[]> {
    const params = this.buildAnalyticsParams(filters);
    params.set('limit', limit.toString());
    const response = await axios.get(
      `${API_URL}/analytics/feedback?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data || response.data;
  }

  /**
   * Get premium analytics pricing
   */
  async getPremiumPricing(): Promise<PremiumAnalyticsPricing> {
    const response = await axios.get(
      `${API_URL}/analytics/premium/pricing`,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Get active premium analytics report
   */
  async getPremiumReport(): Promise<{ active: boolean; report?: PremiumAnalyticsReport }> {
    const response = await axios.get(
      `${API_URL}/analytics/premium/report`,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Purchase premium analytics report access
   */
  async purchasePremiumReport(payload: {
    days?: number;
    filters?: AnalyticsQueryFilters;
    consentAccepted: boolean;
  }): Promise<{ report: PremiumAnalyticsReport; alreadyActive: boolean; pricing: PremiumAnalyticsPricing }> {
    const response = await axios.post(
      `${API_URL}/analytics/premium/purchase`,
      payload,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Cancel active premium analytics access
   */
  async cancelPremiumReport(): Promise<{ cancelled: boolean; report?: PremiumAnalyticsReport }> {
    const response = await axios.post(
      `${API_URL}/analytics/premium/cancel`,
      {},
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Get gas analytics overview
   */
  async getGasAnalytics(filters?: {
    startDate?: string;
    endDate?: string;
    operationType?: string;
  }): Promise<GasAnalytics> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.operationType) params.append('operationType', filters.operationType);

    const response = await axios.get(
      `${API_URL}/gas-analytics/overview?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Get gas transaction history
   */
  async getGasTransactions(filters?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    transactions: TransactionCost[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await axios.get(
      `${API_URL}/gas-analytics/transactions?${params.toString()}`,
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Get gas consumption trends
   */
  async getGasTrends(period: 'daily' | 'weekly' | 'monthly' = 'daily', days: number = 30): Promise<Array<{
    date: string;
    totalCostHBAR: number;
    totalCostUSD: number;
    totalCostINR: number;
    transactionCount: number;
  }>> {
    const response = await axios.get(
      `${API_URL}/gas-analytics/trends?period=${period}&days=${days}`,
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Get operation breakdown
   */
  async getOperationBreakdown(): Promise<Array<{
    operationType: string;
    totalTransactions: number;
    totalCostHBAR: number;
    totalCostUSD: number;
    totalCostINR: number;
    averageCostHBAR: number;
    averageCostUSD: number;
    averageCostINR: number;
    minCostHBAR: number;
    maxCostHBAR: number;
  }>> {
    const response = await axios.get(
      `${API_URL}/gas-analytics/operations`,
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Get benchmark data
   */
  async getBenchmarkData(): Promise<BenchmarkData> {
    const response = await axios.get(
      `${API_URL}/gas-analytics/benchmarks`,
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Get enhanced system statistics
   */
  async getSystemStatistics(manufacturerId?: string, filters?: {
    startDate?: string;
    endDate?: string;
  }): Promise<EnhancedSystemStatistics> {
    const params = new URLSearchParams();
    if (manufacturerId) params.append('manufacturerId', manufacturerId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    // Check if user is admin by looking at stored user data
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const isAdmin = user?.roleLevel === -1;

    // Use admin endpoint if user is admin, otherwise use regular endpoint
    const endpoint = isAdmin 
      ? `${API_URL}/admin/gas-analytics/statistics?${params.toString()}`
      : `${API_URL}/gas-analytics/statistics?${params.toString()}`;

    const response = await axios.get(endpoint, this.getAuthHeader());
    return response.data.data;
  }

  /**
   * Get current exchange rates
   */
  async getCurrentExchangeRates(): Promise<ExchangeRates> {
    const response = await axios.get(
      `${API_URL}/currency/rates/current`,
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Convert HBAR to USD and INR
   */
  async convertHBAR(amount: number): Promise<{
    hbarAmount: number;
    usd: { amount: number; exchangeRate: number };
    inr: { amount: number; exchangeRate: number };
    timestamp: string;
  }> {
    const response = await axios.post(
      `${API_URL}/currency/convert/hbar`,
      { amount },
      this.getAuthHeader()
    );
    return response.data.data;
  }

  /**
   * Export analytics data as CSV
   */
  async exportToCSV(includeGasData: boolean = false, filters?: AnalyticsQueryFilters): Promise<void> {
    const endpoint = includeGasData ? '/gas-analytics/export' : '/analytics/export';
    const params = includeGasData ? new URLSearchParams() : this.buildAnalyticsParams(filters);
    const response = await axios.get(
      `${API_URL}${endpoint}${params.toString() ? `?${params.toString()}` : ''}`,
      {
        ...this.getAuthHeader(),
        responseType: 'blob',
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = includeGasData 
      ? `gas-analytics-${new Date().toISOString().split('T')[0]}.csv`
      : `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  /**
   * Export gas analytics data as CSV
   */
  async exportGasAnalyticsToCSV(): Promise<void> {
    return this.exportToCSV(true);
  }
}

export const analyticsService = new AnalyticsService();
